import { TableTypes } from "@components/Commons/ColumnSelector/ColumnSelectorDialog";
import { CooperationChannels } from "@lib/api-hooks/useCooperations";
import { FixedKpiMetric, KpiMetric } from "@lib/api-hooks";
import type { AdAccountChannels } from "@lib/api-hooks/useAdAccounts/types";
import { Mutable } from "@lib/util-types";
import {
  AVAILABLE_MARKETING_CHANNEL_OPTIONS,
  CHANNELS_SUPPORTING_BREAKDOWN,
  FilterTypesEnum,
  PRESET_STATUS_ENUM,
  TOUCHPOINT_OPTIONS,
} from "@tracify/shared";
import { ChartBarSquareIcon } from "@heroicons/react/24/outline";
import ChartLineIcon from "@components/Layout/icons/chart-line";

export const TRACIFY_APP_STATE_VAR = "TRACIFY_APP_STATE";

export const GIT_REPO_SLUG = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF;
export const MARKETING_CHANNELS_VIEWS = ["ads-manager", "dashboard"];
export const MARKETING_CHANNELS_VIEW_VAR = "MARKETING_CHANNELS_VIEW";
export const DEFAULT_MARKETING_CHANNELS_VIEW: (typeof MARKETING_CHANNELS_VIEWS)[number] =
  "dashboard";

export const MICROSOFT_ADS_EARLY_ACCESS_FLAG_KEY = "early_access_test1";

export const REALTIME_EARLY_ACCESS_FLAG_KEY = "realtime_dashboard_test";

export const DEPLOY_ENVIRONMENT =
  GIT_REPO_SLUG === "main"
    ? "production"
    : GIT_REPO_SLUG === "beta"
      ? "beta"
      : GIT_REPO_SLUG === "demo"
        ? "demo"
        : "development";

export const DEFAULT_SORTING = [{ id: "total_spend", desc: true }];

export const START_DATE_RANGE_SELECT_OPTIONS_BASIC = [
  {
    label: "Today",
    value: "today",
    description: "Data from 00:00 today until now",
  },
  {
    label: "Yesterday",
    value: "yesterday",
    description: "Data for the entire day of yesterday",
  },
  {
    label: "Today & Yesterday",
    value: "today_and_yesterday",
    description: "Data from yesterday 00:00 until now",
  },
  {
    label: "Last 7 days",
    value: "last_7_days",
    description: "Data for the last 7 days excluding today",
  },
  {
    label: "Last 14 days",
    value: "last_14_days",
    description: "Data for the last 14 days excluding today",
  },
  {
    label: "Last 28 days",
    value: "last_28_days",
    description: "Data for the last 28 days excluding today",
  },
  {
    label: "Last 30 days",
    value: "last_30_days",
    description: "Data for the last 30 days excluding today",
  },
  {
    label: "This Week",
    value: "this_week",
    description: "Data from Monday of this week until yesterday",
  },
  {
    label: "Last Week",
    value: "last_week",
    description: "Data for the entire previous week (Monday to Sunday)",
  },
  {
    label: "This Month",
    value: "this_month",
    description: "Data from the 1st of this month until yesterday",
  },
  {
    label: "Last Month",
    value: "last_month",
    description: "Data for the entire previous month",
  },
] as const;

export const START_DATE_RANGE_SELECT_OPTIONS_EXTRA = [
  {
    label: "Last 90 days",
    value: "last_90_days",
    description: "Data for the last 90 days excluding today",
  },
  {
    label: "Last 3 months",
    value: "last_3_months",
    description: "Data for the last 3 months excluding today",
  },
  {
    label: "Last 180 days",
    value: "last_180_days",
    description: "Data for the last 180 days excluding today",
  },
  {
    label: "Last 6 months",
    value: "last_6_months",
    description: "Data for the last 6 months excluding today",
  },
  {
    label: "Year to Date",
    value: "year_to_date",
    description: "Data from Jan 1st of this year until yesterday",
  },
  {
    label: "Last 1 year",
    value: "last_1_year",
    description: "Data for the last 1 year excluding today",
  },
  {
    label: "Custom",
    value: "Custom",
    description: "Select a custom date range",
  },
] as const;

export const START_DATE_RANGE_SELECT_OPTIONS_FULL = [
  {
    label: "Today",
    value: "today",
    description: "Data from 00:00 today until now",
  },
  {
    label: "Yesterday",
    value: "yesterday",
    description: "Data for the entire day of yesterday",
  },
  {
    label: "Today & Yesterday",
    value: "today_and_yesterday",
    description: "Data from yesterday 00:00 until now",
  },
  {
    label: "This Week",
    value: "this_week",
    description: "Data from Monday of this week until yesterday",
  },
  {
    label: "Last Week",
    value: "last_week",
    description: "Data for the entire previous week (Monday to Sunday)",
  },
  {
    label: "Last 7 days",
    value: "last_7_days",
    description: "Data for the last 7 days excluding today",
  },
  {
    label: "Last 10 days",
    value: "last_10_days",
    description: "Data for the last 10 days excluding today",
  },
  {
    label: "Last 14 days",
    value: "last_14_days",
    description: "Data for the last 14 days excluding today",
  },
  {
    label: "Last 15 days",
    value: "last_15_days",
    description: "Data for the last 15 days excluding today",
  },
  {
    label: "Last 28 days",
    value: "last_28_days",
    description: "Data for the last 28 days excluding today",
  },
  {
    label: "Last 30 days",
    value: "last_30_days",
    description: "Data for the last 30 days excluding today",
  },
  {
    label: "Last 90 days",
    value: "last_90_days",
    description: "Data for the last 90 days excluding today",
  },
  {
    label: "Last 180 days",
    value: "last_180_days",
    description: "Data for the last 180 days excluding today",
  },
  {
    label: "This Month",
    value: "this_month",
    description: "Data from the 1st of this month until yesterday",
  },
  {
    label: "Last Month",
    value: "last_month",
    description: "Data for the entire previous month",
  },
  {
    label: "Last 3 months",
    value: "last_3_months",
    description: "Data for the last 3 months excluding today",
  },
  {
    label: "Last 6 months",
    value: "last_6_months",
    description: "Data for the last 6 months excluding today",
  },
  {
    label: "Year to Date",
    value: "year_to_date",
    description: "Data from Jan 1st of this year until yesterday",
  },
  {
    label: "Last 1 year",
    value: "last_1_year",
    description: "Data for the last 1 year excluding today",
  },
  {
    label: "Custom",
    value: "Custom",
    description: "Select a custom date range",
  },
] as const;

export const PREVIOUS_DATE_RANGE_SELECT_OPTIONS = [
  {
    label: "No Comparison",
    value: "no_comparison",
    description: "Don't compare with a previous period",
  },
  {
    label: "Previous Period",
    value: "last_period",
    description:
      "Compare with the period immediately before the selected range",
  },
  {
    label: "1 Week Ago",
    value: "last_week",
    description: "Compare with the same range shifted back by 1 week",
  },
  {
    label: "1 Month Ago",
    value: "last_month",
    description: "Compare with the same range shifted back by 1 month",
  },
  {
    label: "1 Quarter Ago",
    value: "last_quarter",
    description: "Compare with the same range shifted back by 1 quarter",
  },
  {
    label: "1 Year Ago",
    value: "last_year",
    description: "Compare with the same range shifted back by 1 year",
  },
  {
    label: "Custom",
    value: "custom",
    description: "Select a custom comparison range",
  },
] as const;

export const ATTRIBUTION_WINDOW_OPTIONS = [
  {
    label: "1 day",
    value: "1",
  },
  {
    label: "2 days",
    value: "2",
  },
  {
    label: "7 days",
    value: "7",
  },
  {
    label: "14 days",
    value: "14",
  },
  {
    label: "28 days",
    value: "28",
  },
  {
    label: "60 days",
    value: "60",
  },
  {
    label: "90 days",
    value: "90",
  },
] as const;

export const ATTRIBUTION_MODEL_OPTIONS = [
  {
    label: "AI Attribution",
    value: "dda_stcb_purchaseatd_t3",
    description:
      "Understand the true impact of each touchpoint on all conversions",
    detailedDescription:
      "Our AI Attribution automatically calculates the impact of all touchpoints across your customer's journey; standard attribution settings no longer need to be configured.",
  },
  {
    label: "First Click",
    value: "first",
    description: "Best for campaigns driving initial customer interest",
    detailedDescription:
      "This model gives 100% of the credit for a sale to the first touchpoint a customer had with your business.",
  },
  {
    label: "Last Click",
    value: "last",
    description: "Best for campaigns directly leading to conversions",
    detailedDescription:
      "This model gives 100% of the credit for a sale to the last touchpoint a customer had with your business.",
  },
  {
    label: "U-Shape",
    value: "ushape",
    description: "For emphasizing initial engagement and final conversion",
    detailedDescription:
      "This model gives 100% of the credit for a sale to the first touchpoint a customer had with your business.",
  },
  {
    label: "Linear",
    value: "linear",
    description: "For strategies where every touchpoint is considered equal",
    detailedDescription:
      "This model gives 100% of the credit for a sale to the first touchpoint a customer had with your business.",
  },

  {
    label: "Isolated",
    value: "isolated",
    description: "Give 100% attribution credit to",
    detailedDescription: "",
  },
] as const;

export const CHANNELS_WITHOUT_BASESCORE_TYPE = [
  "influencer",
  "influencer_module",
  "email",
  "referred",
  "referral",
  "whatsapp",
  "direct",
  "organic",
];

export const ADVERTISING_CHANNEL_TYPES_WITHOUT_ADS = [
  "PERFORMANCE_MAX",
  "SMART",
];

export const CHANNELS_WITHOUT_ADSETS_TYPE = ["outbrain", "criteo", "taboola"];

export type ChannelsSupportingBreakdown = typeof CHANNELS_SUPPORTING_BREAKDOWN;
const PAID_MEDIA_EXCLUDED_CHANNELS = new Set([
  "direct",
  "organic",
  "referral",
  "referred",
  "invalid",
]);

export const getPaidMediaChannels = <T extends { value: string }>(
  channels: readonly T[]
) =>
  channels.filter(
    (channel) => !PAID_MEDIA_EXCLUDED_CHANNELS.has(channel.value)
  );

export const PAID_CHANNELS = getPaidMediaChannels(
  AVAILABLE_MARKETING_CHANNEL_OPTIONS
);

export const CHANNEL_SELECT_OPTIONS = [
  {
    label: "All",
    value: "all",
    description: "Analyze paid and non-paid touchpoints",
    channels: AVAILABLE_MARKETING_CHANNEL_OPTIONS,
    hasSubMenu: false,
  },
  {
    label: "Paid Media",
    value: "paid",
    description: "Analyze all touchpoints except Organic and Direct",
    channels: PAID_CHANNELS,
    hasSubMenu: false,
  },
  {
    label: "Custom",
    value: "custom",
    description: "Choose which touchpoints to include",
    channels: AVAILABLE_MARKETING_CHANNEL_OPTIONS,
    hasSubMenu: true,
  },
] as const;

export const AVAILABLE_INFLUENCER_SOURCE_OPTIONS = [
  {
    label: "TikTok",
    value: "tiktok",
  },
  {
    label: "Facebook",
    value: "facebook",
  },
  {
    label: "Instagram",
    value: "instagram",
  },
  {
    label: "YouTube",
    value: "youtube",
  },
  {
    label: "Blog",
    value: "blog",
  },
  {
    label: "Twitch",
    value: "twitch",
  },
  {
    label: "Pinterest",
    value: "pinterest",
  },
  {
    label: "LinkedIn",
    value: "linkedin",
  },
  {
    label: "Snapchat",
    value: "snapchat",
  },
  {
    label: "Podcast",
    value: "podcast",
  },
  {
    label: "Other",
    value: "other",
  },
];

export const AVAILABLE_GOOGLE_CHANNEL_OPTIONS = [
  {
    label: "Perf. Max",
    value: "PERFORMANCE_MAX",
  },
  {
    label: "Search",
    value: "SEARCH",
  },
  {
    label: "Demand Gen",
    value: "DEMAND_GEN",
  },
  {
    label: "Display",
    value: "DISPLAY",
  },
  {
    label: "Shopping",
    value: "SHOPPING",
  },
  {
    label: "Local",
    value: "LOCAL",
  },
  {
    label: "Local Services",
    value: "LOCAL_SERVICES",
  },
  {
    label: "Multi Channel",
    value: "MULTI_CHANNEL",
  },
  {
    label: "Smart",
    value: "SMART",
  },
  {
    label: "Unknown",
    value: "UNKNOWN",
  },
  {
    label: "Unspecified",
    value: "UNSPECIFIED",
  },
  {
    label: "Video",
    value: "VIDEO",
  },
  {
    label: "Catalog Sales",
    value: "CATALOG_SALES",
  },
] as const;

export const REPORT_TIME_OPTIONS = [
  {
    label: "Conversion Date",
    value: "event",
  },
  {
    label: "Click Date",
    value: "adview",
  },
] as const;

export const PPS_METRICS_OPTIONS = [
  { label: "Revenue", value: "customer_revenue" },
  { label: "Orders", value: "customer_purchase" },
  { label: "ROAS", value: "return_on_ad_spend" },
  { label: "AOV", value: "average_order_value" },
  { label: "CPO", value: "cost_per_order" },
  { label: "CAC", value: "customer_acquisition_cost" },
];

export const NVR_OPTIONS = [
  {
    label: "All Customers",
    title: "All Customers",
    value: "total",
    description:
      "View combined metrics for new and returning customers, including the breakdown between the two.",
  },
  {
    label: "New Customers",
    title: "New Customers",
    value: "new",
    description: "View metrics for first-time customers only.",
  },
  {
    label: "Returning Customers",
    title: "Returning Customers",
    value: "returning",
    description: "View metrics for customers who have purchased before.",
  },
] as const;

export const AVAILABLE_COLUMNS_PER_TABLE: Record<
  TableTypes,
  Array<(typeof COLUMN_OPTIONS)[number]["value"]>
> = {
  realtime: [
    "customer_purchase",
    "customer_revenue",
    "average_order_value",
    "conversion_rate",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
  ],
  marketingChannelOverview: [
    "historical",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "average_order_value",
    "conversion_rate",
    "customer_acquisition_cost",
    "cost_per_order",
    "cost_per_click",
    "cost_per_view",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
  ],
  creativesOverview: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
    "historical",
  ],
  pinterestCreativesOverview: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
    "historical",
  ],
  outbrainCreativesOverview: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
    "historical",
  ],
  googleCreativesOverview: [
    "spend",
    "purchaseCount",
    "purchaseAmount",
    "roas",
    "cac",
    "cpo",
    "ctr",
    "aov",
    "cr",
    "cpm",
    "linkClick",
    "cpc",
    "addtocart",
    "productview",
    "pageview",
    "totalAddtocart",
    "totalProductview",
    "totalPageview",
    "impressions",
    "newCustomerRate",
    "newVisitorRate",
    "historical",
  ],
  taboolaCreativesOverview: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
    "historical",
  ],
  facebookCreativesOverview: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "frequency",
    "link_click",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "reach",
    "impressions",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "thumbstop_ratio",
    "video_avg_time_watched_actions",
    "video_view",
    "post",
    "comment",
    "onsite_conversion_post_save",
    "historical",
  ],
  msadsCreativesOverview: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "link_click",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "historical",
  ],
  googleChannels: [
    "historical",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
  ],
  adsManager: [
    "historical",
    "campaign_id",
    "adset_id",
    "ad_id",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
  ],
  pinterestAdsManager: [
    "historical",
    "campaign_id",
    "adset_id",
    "ad_id",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
  ],
  outbrainAdsManager: [
    "historical",
    "campaign_id",
    "ad_id",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
  ],
  taboolaAdsManager: [
    "historical",
    "campaign_id",
    "ad_id",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
  ],
  facebookAdsManager: [
    "historical",
    "campaign_id",
    "adset_id",
    "ad_id",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "link_click",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
    "reach",
    "frequency",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
  ],
  msadsAdsManager: [
    "historical",
    "campaign_id",
    "adset_id",
    "ad_id",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",

    "clicks",
  ],
  googleAdsManager: [
    "historical",
    "campaign_id",
    "adset_id",
    "ad_id",
    "keyword_id",
    "asset_group_id",
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
    "conversions",
    "all_conversions",
  ],
  influencer: [
    "customer_purchase",
    "customer_revenue",
    "total_spend",
    "influencer_fixed_commission",
    "influencer_variable_commission",
    "discountMatchingRate",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "average_order_value",
    "conversion_rate",
    "cost_per_order",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    // "name",
  ],
  cooperations: [
    "customer_purchase",
    "customer_revenue",
    "total_spend",
    "labels",
    "influencer_fixed_commission",
    "influencer_variable_commission",
    "discountMatchingRate",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "average_order_value",
    "conversion_rate",
    "cost_per_order",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    // "name",
  ],
  influencerChannelOverview: [
    "historical",
    "average_order_value",
    "conversion_rate",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_unique_addtocart",
    "customer_unique_pageview",
    "customer_unique_productview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "customer_purchase",
    "customer_revenue",
    "total_spend",
  ],
  creativeComparison: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "cost_per_click",
    "cost_per_mille",
    "click_through_rate",
    "clicks",
    "average_order_value",
    "conversion_rate",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "customer_unique_addtocart",
    "impressions",
    "customer_unique_productview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "customer_unique_pageview",
  ],
  facebookCreativeComparison: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "cost_per_click",
    "cost_per_mille",
    "click_through_rate",
    "link_click",
    "average_order_value",
    "conversion_rate",
    "frequency",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "customer_unique_addtocart",
    "thumbstop_ratio",
    "comment",
    "onsite_conversion_post_save",
    "post",
    "reach",
    "impressions",
    "video_avg_time_watched_actions",
    "video_view",
    "customer_unique_productview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "customer_unique_pageview",
    "historical",
  ],
  facebookCreativeAnalysis: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "frequency",
    "link_click",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "reach",
    "impressions",
    "comment",
    "onsite_conversion_post_save",
    "post",
    "video_avg_time_watched_actions",
    "video_view",
    "thumbstop_ratio",
  ],
  creativeAnalysis: [
    "total_spend",
    "customer_purchase",
    "customer_revenue",
    "return_on_ad_spend",
    "acquisition_return_on_ad_spend",
    "customer_acquisition_cost",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "cost_per_order",
    "click_through_rate",
    "average_order_value",
    "conversion_rate",
    "cost_per_mille",
    "clicks",
    "cost_per_click",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
    "impressions",
  ],
  customChannelManager: [
    "historical",
    "customer_purchase",
    "customer_revenue",
    "new_customer_purchase_ratio",
    "new_visitor_pageview_ratio",
    "average_order_value",
    "conversion_rate",
    "clicks",
    "customer_unique_addtocart",
    "customer_unique_productview",
    "customer_unique_pageview",
    "customer_addtocart",
    "customer_productview",
    "customer_pageview",
  ],
};

export const DEFAULT_COLUMNS_PER_TABLE = {
  ...AVAILABLE_COLUMNS_PER_TABLE,
  marketingChannelOverview:
    AVAILABLE_COLUMNS_PER_TABLE.marketingChannelOverview.filter(
      (metric) => metric !== "cost_per_click" && metric !== "cost_per_view"
    ),
};

export const maxMetricCountForTables: Record<string, number> = {
  facebookCreativeAnalysis: 10,
  creativeAnalysis: 10,
};

export const chartTooltipStyles = {
  backgroundColor: "hsl(var(--popover))",
  borderColor: "hsl(var(--border))",
  borderRadius: "0.5rem",
  padding: "0.75rem",
  fontSize: "0.875rem",
};

export const CONSTANT_LABELS: { [key: string]: string } = {
  meta: "Meta",
  facebook: "Meta",
  google: "Google",
  tiktok: "TikTok",
  instagram: "Instagram",
  threads: "Threads",
  twitter: "Twitter / X",
  microsoft: "Microsoft",
  msads: "Microsoft",
  linkedin: "LinkedIn",
  snapchat: "Snapchat",
  youtube: "YouTube",
  twitch: "Twitch",
  podcast: "Podcast",
  blog: "Blog",
  other: "Other",
  email: "Email",
  influencer: "Influencer",
  organic: "Organic",
  referred: "Organic",
  referral: "Referral",
  taboola: "Taboola",
  outbrain: "Outbrain",
  pinterest: "Pinterest",
  whatsapp: "WhatsApp",
  direct: "Direct",
  criteo: "Criteo",
  historical: "Historical Chart",
  new_customers: "New Customers",
  returning_customers: "Returning Customers",
  all_customers: "All Customers",
  story: "Story",
  post: "Post",
  reel: "Reel",
  live: "Live",
  video: "Video",
  image: "Image",
  short: "Short",
  pin: "Pin",
  customer_revenue: "Revenue",
  ...Object.fromEntries(
    AVAILABLE_GOOGLE_CHANNEL_OPTIONS.map((o) => [o.value, o.label])
  ),
};

export const GENERAL_CONSTANT_LABELS: { [key: string]: string } = {
  ...CONSTANT_LABELS,
  cost_per_order: "CPA",
  average_order_value: "ACV",
  customer_revenue: "Conv. Value",
};

export const METRIC_MULTIPLIER: Partial<
  Record<keyof KpiMetric | keyof FixedKpiMetric, number>
> = {
  click_through_rate: 100,
  conversion_rate: 100,
  new_customer_purchase_ratio: 100,
  new_visitor_pageview_ratio: 100,
  thumbstop_ratio: 100,
};

export const CHANNEL_URL_PARAMS: { [key in CooperationChannels]: string } = {
  facebook: "fb",
  tiktok: "tt",
  instagram: "ig",
  linkedin: "lin",
  snapchat: "sc",
  youtube: "yt",
  twitch: "ttv",
  podcast: "pod",
  blog: "blg",
  pinterest: "pin",
  twx: "twx",
  threads: "thr",
  other: "other",
};

export const AXIS_VALUES = [
  1, 1.5, 2, 3, 4, 5, 6, 8, 10, 25, 50, 75, 100, 250, 500, 750, 1000, 2500,
  5000, 7500, 10000, 25000, 50000, 75000, 100000, 250000, 500000, 750000,
  1000000, 2500000, 5000000, 7500000, 10000000, 15000000, 20000000,
];
export const FINE_GRAINED_AXIS_VALUES = [
  1, 1.5, 2, 3, 4, 5, 6, 8, 10, 12, 15, 18, 20, 25, 30, 40, 50, 75, 100, 125,
  150, 200, 250, 300, 400, 500, 750, 1000, 1250, 1500, 2000, 2500, 3000, 4000,
  5000, 7500, 10000, 12500, 15000, 20000, 25000, 30000, 40000, 50000, 75000,
  100000, 125000, 150000, 200000, 250000, 400000, 500000, 750000, 1000000,
  1250000, 1500000, 2000000, 2500000, 3000000, 4000000, 5000000, 7500000,
  10000000, 12500000, 15000000, 20000000,
];

export const DASHBOARD_APPROVALS =
  DEPLOY_ENVIRONMENT === "beta"
    ? ["access_rts_dashboard", "access_beta_environment"]
    : ["access_rts_dashboard"];

export const ECOMMERCE_TO_GENERAL_LABEL_MAPPING = {
  Revenue: "Conv. Value",
  Orders: "Conversions",
  ROAS: "ROAS",
  CPO: "CPA",
};

// channel has to be defined in the value prop before _
// because we use it in other functions
export const INFLUENCER_PLACEMENT_TYPES = [
  { label: "Instagram Story", value: "instagram_story" },
  { label: "Instagram Post", value: "instagram_post" },
  { label: "Instagram Reel", value: "instagram_reel" },
  { label: "Instagram Live", value: "instagram_live" },
  { label: "TikTok Post", value: "tiktok_post" },
  { label: "TikTok Story", value: "tiktok_story" },
  { label: "Youtube Video", value: "youtube_video" },
  { label: "Youtube Short", value: "youtube_short" },
  { label: "Twitch Live", value: "twitch_live" },
  { label: "Pinterest Pin", value: "pinterest_pin" },
  { label: "Blog Post", value: "blog_post" },
  { label: "Podcast", value: "podcast" },
  { label: "LinkedIn Post", value: "linkedin_post" },
  { label: "Snapchat Story", value: "snapchat_story" },
  { label: "Facebook Post", value: "facebook_post" },
  { label: "Facebook Story", value: "facebook_story" },
  { label: "Twitter / X Post", value: "twx_post" },
  { label: "Threads Post", value: "threads_post" },
  { label: "Other", value: "other_post" },
] as const;

export const creativeComparisonColors = [
  "text-[#B8AEFF] dark:text-[#6254FF]",
  "text-[#A7C7E5] dark:text-[#4D73A3]",
  "text-[#DDAAA5] dark:text-[#9E5A54]",
  "text-[#A4E5C6] dark:text-[#46A070]",
  "text-[#D1A6C6] dark:text-[#AD648D]",
  "text-[#9EC1CC] dark:text-[#6491A3]",
  "text-[#B79FCC] dark:text-[#9267A6]",
  "text-[#A088C1] dark:text-[#7657A4]",
];

export const adsChartColors = [
  "text-[#547CFF]",
  "text-[#54B2FF]",
  "text-[#D254FF]",
  "text-[#B0A9FF]",
];

export const lightCreativeComparisonColors = [
  "#B8AEFF",
  "#A7C7E5",
  "#DDAAA5",
  "#A4E5C6",
  "#D1A6C6",
  "#9EC1CC",
  "#B79FCC",
  "#A088C1",
];

export const darkCreativeComparisonColors = [
  "#6254FF",
  "#4D73A3",
  "#9E5A54",
  "#46A070",
  "#AD648D",
  "#6491A3",
  "#9267A6",
  "#7657A4",
];

export const creativeComparisonGradients = [
  "bg-linear-to-r from-[#B8AEFF] dark:from-[#6254FF]",
  "bg-linear-to-r from-[#A7C7E5] dark:from-[#4D73A3]",
  "bg-linear-to-r from-[#DDAAA5] dark:from-[#9E5A54]",
  "bg-linear-to-r from-[#A4E5C6] dark:from-[#46A070]",
  "bg-linear-to-r from-[#D1A6C6] dark:from-[#AD648D]",
  "bg-linear-to-r from-[#9EC1CC] dark:from-[#6491A3]",
  "bg-linear-to-r from-[#B79FCC] dark:from-[#9267A6]",
  "bg-linear-to-r from-[#A088C1] dark:from-[#7657A4]",
];

export const CREATIVE_CHART_OPTIONS = [
  { label: "Bar Chart", value: "bar", icon: ChartBarSquareIcon },
  {
    label: "Line Chart",
    value: "line",
    icon: ChartLineIcon,
  },
];

export const pageLookup = {
  "/": "realtime dashboard",
  "/channels/meta/ads-manager": "meta ads manager",
  "/channels/google/ads-manager": "google ads manager",
  "/channels/tiktok/ads-manager": "tiktok ads manager",
  "/channels/pinterest/ads-manager": "pinterest ads manager",
  "/channels/outbrain/ads-manager": "outbrain ads manager",
  "/channels/taboola/ads-manager": "taboola ads manager",
};

export const EDUCATION_BANNER = {
  REALTIME_DASHBOARD: {
    video: "https://youtu.be/d2taLIJyzTc",
    article: undefined,
  },
  MARKETING_OVERVIEW: {
    video: "https://youtu.be/-JO49GmHWwA",
    article: undefined,
  },
  INFLUENCER_MODULE: {
    video: "https://www.youtube.com/watch?v=weDJoRIOIHk",
    article:
      "https://knowledge.tracify.ai/en/collections/46362-influencer-dashboard",
  },
  CREATIVE_DASHBOARD: {
    video: "https://www.youtube.com/watch?v=u1yGpm9yFRk",
    article:
      "https://knowledge.tracify.ai/en/collections/126864-creative-dashboard",
  },
  CUSTOMER_JOURNEYS: {
    video: "https://www.youtube.com/watch?v=3TFK649yXGQ",
    article:
      "https://knowledge.tracify.ai/en/collections/126900-customer-journey-dashboard",
  },
  MARKETING_GOOGLE: {
    video: "https://youtu.be/etAy1lo7RjE",
    article: undefined,
  },
  MARKETING_META: {
    video: "https://youtu.be/EdE3X743eg0",
    article: undefined,
  },
  MARKETING_TIKTOK: {
    video: "https://youtu.be/93zdKE082_A",
    article: undefined,
  },
  MARKETING_PINTEREST: {
    video: "https://youtu.be/93zdKE082_A",
    article: undefined,
  },
  PROFIT_AND_LOSS: {
    video: "https://youtu.be/klDHpdvO92w",
    article:
      "https://knowledge.tracify.ai/en/collections/591447-profit-loss-pnl-dashboard",
  },
};

export type KPI_METRIC_KEYS = keyof KpiMetric | keyof FixedKpiMetric;

export const METRICS_LABELS = {
  ad_spend: "Ad Spend",
  influencer_fixed_commission: "Fixed Commissions",
  clicks: "Clicks",
  link_click: "Clicks (link)",
  important_clicks: "Important Clicks",
  impressions: "Impressions",
  reach: "Reach",
  comment: "Comments",
  landing_page_view: "Landing Page Views",
  conversions: "Conversions", // Replaces platformConversions
  all_conversions: "All Conversions", // Replaces allPlatformConversions
  video_view: "Video Views",
  onsite_conversion_post_save: "Post Saves",
  video_avg_time_watched_actions: "Avg. Watch Time",
  click_through_rate: "CTR",
  frequency: "Frequency",
  thumbstop_ratio: "Thumbstop Ratio",
  cost_per_click: "CPC",
  cost_per_view: "Cost per View",
  cost_per_mille: "CPM",
  customer_purchase: "Orders",
  customer_revenue: "Revenue",
  customer_num_items: "Items Sold",
  customer_pageview: "Page Views",
  customer_unique_pageview: "Unique Page Views",
  customer_productview: "Content Views",
  customer_unique_productview: "Unique Content Views",
  customer_addtocart: "Adds to Cart",
  customer_unique_addtocart: "Unique Adds to Cart",
  customer_checkout: "Checkouts Initiated",
  customer_unique_checkout: "Unique Checkouts Initiated",
  influencer_variable_commission: "Variable Commissions",
  total_spend: "Ad Spend",
  return_on_ad_spend: "ROAS",
  acquisition_return_on_ad_spend: "Acquisition ROAS",
  cost_per_order: "CPO",
  conversion_rate: "Conversion Rate",
  average_order_value: "AOV",
  customer_acquisition_cost: "CAC",
  new_customer_purchase_ratio: "New Customer Rate",
  new_visitor_pageview_ratio: "New Visitor Rate",
  campaign_id: "Campaign ID",
  adset_id: "Adset/Group ID",
  ad_id: "Ad ID",
  keyword_id: "Keyword ID",
  asset_group_id: "Asset group ID",
  historical: "Historical Chart (Spend)",
  marketing_efficiency_ratio: "MER",
  new_customers: "New Customers",
  returning_customers: "Returning Customers",
  all_customers: "All Customers",
};

export const GENERAL_METRICS_LABELS = {
  ...METRICS_LABELS,
  customer_revenue: "Conv. Value",
  customer_purchase: "Conversions",
  cost_per_order: "CPA",
  average_order_value: "ACV",
};

export const getMetricLabelsForDashboardMode = (dashboardMode?: string) =>
  dashboardMode === "general" ? GENERAL_METRICS_LABELS : METRICS_LABELS;

export const getMetricLabelForDashboardMode = (
  metric: keyof typeof METRICS_LABELS | string,
  dashboardMode?: string
) =>
  getMetricLabelsForDashboardMode(dashboardMode)[
    metric as keyof typeof METRICS_LABELS
  ] ?? metric;

export const FILTER_TO_API_MAPPING = {
  format: "creative_type",
  creativeFormat: "creative_type",
  keyword_name: "keyword_text",
};

export const COLUMN_OPTIONS = [
  { label: "Ad Spend", value: "ad_spend" },
  { label: "Ad Spend", value: "total_spend" },
  { label: "Historical Chart", value: "historical" },
  { label: "Fixed Commissions", value: "influencer_fixed_commission" },
  { label: "Variable Commissions", value: "influencer_variable_commission" },
  { label: "Orders", value: "customer_purchase" },
  { label: "Labels", value: "labels" },
  { label: "Revenue", value: "customer_revenue" },
  { label: "ROAS", value: "return_on_ad_spend" },
  { label: "Acquisition ROAS", value: "acquisition_return_on_ad_spend" },
  { label: "Ad Spend", value: "cost" },
  { label: "CAC", value: "customer_acquisition_cost" },
  { label: "New Customer Rate", value: "new_customer_purchase_ratio" },
  { label: "New Visitor Rate", value: "new_visitor_pageview_ratio" },
  { label: "CPO", value: "cost_per_order" },
  { label: "CTR", value: "click_through_rate" },
  { label: "CTR (link)", value: "link_click_through_rate" },
  { label: "Average Order Value", value: "average_order_value" },
  { label: "Conversion Rate", value: "conversion_rate" },
  { label: "CPM", value: "cost_per_mille" },
  { label: "Frequency", value: "frequency" },
  { label: "Clicks", value: "clicks" },
  { label: "Link Clicks", value: "link_click" },
  { label: "Important Clicks", value: "important_clicks" },
  { label: "Impressions", value: "impressions" },
  { label: "Reach", value: "reach" },
  { label: "Avg. Watch Time", value: "video_avg_time_watched_actions" },
  { label: "Post Saves", value: "onsite_conversion_post_save" },
  { label: "Post Shares", value: "post" },
  { label: "CPC", value: "cost_per_click" },
  { label: "Cost per View", value: "cost_per_view" },
  { label: "Google Conversions", value: "conversions" },
  { label: "All Google Conversions", value: "all_conversions" },
  { label: "Unique Adds to Cart", value: "customer_unique_addtocart" },
  { label: "Unique Checkouts Initiated", value: "customer_unique_checkout" },
  { label: "Unique Content Views", value: "customer_unique_productview" },
  { label: "Unique Page Views", value: "customer_unique_pageview" },
  { label: "Adds to Cart", value: "customer_addtocart" },
  { label: "Content Views", value: "customer_productview" },
  { label: "Page Views", value: "customer_pageview" },
  { label: "Time to Sale", value: "avg_conversion_time" },
  { label: "New Customer Rate", value: "new_customer_purchase_ratio" },
  { label: "% of Customers", value: "customer_percentage" },
  { label: "Steps", value: "steps" },
  { label: "Comments", value: "comment" },
  { label: "3s Video Views", value: "video_view" },
  { label: "Thumbstop Ratio", value: "thumbstop_ratio" },
  { label: "Campaign ID", value: "campaign_id" },
  { label: "Adset/Group ID", value: "adset_id" },
  { label: "Ad ID", value: "ad_id" },
  { label: "Keyword ID", value: "keyword_id" },
  { label: "Asset group ID", value: "asset_group_id" },
  { label: "Avg. Time to Conversion", value: "average_seconds_to_conversion" },
  { label: "Number of Customers", value: "number_of_customers" },
];

export const GENERAL_COLUMN_OPTIONS = [
  { label: "Ad Spend", value: "ad_spend" },
  { label: "Ad Spend", value: "total_spend" },
  { label: "Historical Chart", value: "historical" },
  { label: "Fixed Commissions", value: "influencer_fixed_commission" },
  { label: "Variable Commissions", value: "influencer_variable_commission" },
  { label: "Discount Matching Rate", value: "discountMatchingRate" },
  { label: "Conversions", value: "customer_purchase" },
  { label: "Labels", value: "labels" },
  { label: "Conv. Value", value: "customer_revenue" },
  { label: "ROAS", value: "return_on_ad_spend" },
  { label: "Acquisition ROAS", value: "acquisition_return_on_ad_spend" },
  { label: "Ad Spend", value: "cost" },
  { label: "CAC", value: "customer_acquisition_cost" },
  { label: "Google Conversions", value: "conversions" },
  { label: "All Google Conversions", value: "all_conversions" },
  { label: "New Customer Rate", value: "new_customer_purchase_ratio" },
  { label: "New Visitor Rate", value: "new_visitor_pageview_ratio" },
  { label: "CPA", value: "cost_per_order" },
  { label: "CTR", value: "click_through_rate" },
  { label: "CTR (link)", value: "link_click_through_rate" },

  { label: "Average Conversion Value", value: "average_order_value" },
  { label: "Conversion Rate", value: "conversion_rate" },
  { label: "CPM", value: "cost_per_mille" },
  { label: "Frequency", value: "frequency" },
  { label: "Clicks", value: "clicks" },
  { label: "Link Clicks", value: "link_click" },
  { label: "Important Clicks", value: "important_clicks" },
  { label: "Impressions", value: "impressions" },
  { label: "Reach", value: "reach" },
  { label: "Avg. Watch Time", value: "video_avg_time_watched_actions" },
  { label: "Post Saves", value: "onsite_conversion_post_save" },
  { label: "Post Shares", value: "post" },
  { label: "CPC", value: "cost_per_click" },
  { label: "Cost per View", value: "cost_per_view" },
  { label: "Unique Adds to Cart", value: "customer_unique_addtocart" },
  { label: "Unique Checkouts Initiated", value: "customer_unique_checkout" },
  { label: "Unique Content Views", value: "customer_unique_productview" },
  { label: "Unique Page Views", value: "customer_unique_pageview" },
  { label: "Adds to Cart", value: "customer_addtocart" },
  { label: "Content Views", value: "customer_productview" },
  { label: "Page Views", value: "customer_pageview" },
  { label: "Impressions", value: "impressions" },
  { label: "Reach", value: "reach" },
  { label: "Time to Sale", value: "avg_conversion_time" },
  { label: "New Customer Rate", value: "new_customer_purchase_ratio" },
  { label: "% of Customers", value: "customer_percentage" },
  { label: "Steps", value: "steps" },
  { label: "Comments", value: "comment" },
  { label: "Post Saves", value: "onsite_conversion_post_save" },
  { label: "Post Shares", value: "post" },
  { label: "Avg. Watch Time", value: "video_avg_time_watched_actions" },
  { label: "3s Video Views", value: "video_view" },
  { label: "Thumbstop Ratio", value: "thumbstop_ratio" },
  { label: "Campaign ID", value: "campaign_id" },
  { label: "Adset/Group ID", value: "adset_id" },
  { label: "Ad ID", value: "ad_id" },
  { label: "Keyword ID", value: "keyword_id" },
  { label: "Asset group ID", value: "asset_group_id" },
  { label: "Avg. Time to Conversion", value: "average_seconds_to_conversion" },
  { label: "Number of Customers", value: "number_of_customers" },
];

const ADS_MANAGER_ID_FILTERS = [
  { label: "Campaign Id", value: "campaign_id", type: "text" },
  { label: "Adset Id", value: "adset_id", type: "text" },
  { label: "Ad Id", value: "ad_id", type: "text" },
];

export const ADS_MANAGER_FILTERS = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Adset Name",
    value: "adset_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Ad Name",
    value: "ad_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Keyword",
    value: "keyword_text",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Asset group Name",
    value: "asset_group_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  ...ADS_MANAGER_ID_FILTERS,
  { label: "Keyword Id", value: "keyword_id", type: "text" },
  { label: "Asset group Id", value: "asset_group_id", type: "text" },
  { label: "Ad Spend", value: "total_spend", inputLabel: "€", type: "number" },
  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  { label: "Orders", value: "customer_purchase", type: "number" },

  { label: "ROAS", value: "return_on_ad_spend", type: "number" },
  {
    label: "CTR",
    value: "click_through_rate",
    inputLabel: "%",
    type: "number",
  },
  {
    label: "CAC",
    value: "customer_acquisition_cost",
    inputLabel: "€",
    type: "number",
  },
  { label: "CPO", value: "cost_per_order", inputLabel: "€", type: "number" },

  {
    label: "Channel Type",
    value: "advertising_channel_type",
    type: "select",
    availableFilter: [FilterTypesEnum.eq],
    options: AVAILABLE_GOOGLE_CHANNEL_OPTIONS.map((o) => ({
      label: o.label,
      value: o.value,
    })),
  },
];

export const GENERAL_ADS_MANAGER_FILTERS = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Adset Name",
    value: "adset_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Ad Name",
    value: "ad_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Keyword",
    value: "keyword_text",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Asset group Name",
    value: "asset_group_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  ...ADS_MANAGER_ID_FILTERS,
  { label: "Keyword Id", value: "keyword_id", type: "text" },
  { label: "Asset group Id", value: "asset_group_id", type: "text" },
  { label: "Ad Spend", value: "total_spend", inputLabel: "€", type: "number" },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  { label: "Conversions", value: "customer_purchase", type: "number" },
  { label: "ROAS", value: "return_on_ad_spend", type: "number" },
  {
    label: "CTR",
    value: "click_through_rate",
    inputLabel: "%",
    type: "number",
  },
  {
    label: "CAC",
    value: "customer_acquisition_cost",
    inputLabel: "€",
    type: "number",
  },
  { label: "CPA", value: "cost_per_order", inputLabel: "€", type: "number" },

  {
    label: "Channel Type",
    value: "advertising_channel_type",
    type: "select",
    availableFilter: [FilterTypesEnum.eq],
    options: AVAILABLE_GOOGLE_CHANNEL_OPTIONS.map((o) => ({
      label: o.label,
      value: o.value,
    })),
  },
];

export const RT_FILTERS = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Adset Name",
    value: "adset_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Ad Name",
    value: "ad_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  { label: "Campaign Id", value: "campaign_id", type: "text" },
  { label: "Adset Id", value: "adset_id", type: "text" },
  { label: "Ad Id", value: "ad_id", type: "text" },

  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Orders",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const GENERAL_RT_FILTERS = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Adset Name",
    value: "adset_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Ad Name",
    value: "ad_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  { label: "Campaign Id", value: "campaign_id", type: "text" },
  { label: "Adset Id", value: "adset_id", type: "text" },
  { label: "Ad Id", value: "ad_id", type: "text" },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversions",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const CREATIVES_FILTERS = [
  {
    label: "Ad Name",
    value: "ad_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  ...ADS_MANAGER_ID_FILTERS,
  {
    label: "Creative Format",
    value: "format",
    type: "select",
    availableFilter: [FilterTypesEnum.in],
    options: [
      { label: "Image", value: "image" },
      { label: "Video", value: "video" },
    ],
  },
  { label: "Ad Spend", value: "total_spend", inputLabel: "€", type: "number" },
  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  { label: "Orders", value: "customer_purchase", type: "number" },

  { label: "Impressions", value: "impressions", type: "number" },
  { label: "ROAS", value: "return_on_ad_spend", type: "number" },
  {
    label: "CTR",
    value: "click_through_rate",
    inputLabel: "%",
    type: "number",
  },
  {
    label: "CAC",
    value: "customer_acquisition_cost",
    inputLabel: "€",
    type: "number",
  },
  { label: "CPO", value: "cost_per_order", inputLabel: "€", type: "number" },
];

export const GENERAL_CREATIVES_FILTERS = [
  {
    label: "Ad Name",
    value: "ad_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  ...ADS_MANAGER_ID_FILTERS,
  {
    label: "Creative Format",
    value: "creativeFormat",
    type: "select",
    availableFilter: [FilterTypesEnum.in],
    options: [
      { label: "Image", value: "image" },
      { label: "Video", value: "video" },
    ],
  },
  { label: "Ad Spend", value: "total_spend", inputLabel: "€", type: "number" },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  { label: "Conversions", value: "customer_purchase", type: "number" },

  { label: "Impressions", value: "impressions", type: "number" },
  { label: "ROAS", value: "return_on_ad_spend", type: "number" },
  {
    label: "CTR",
    value: "click_through_rate",
    inputLabel: "%",
    type: "number",
  },
  {
    label: "CAC",
    value: "customer_acquisition_cost",
    inputLabel: "€",
    type: "number",
  },
  { label: "CPA", value: "cost_per_order", inputLabel: "€", type: "number" },
];

export const INFLUENCER_FILTERS = [
  {
    label: "Influencer Name",
    value: "influencer_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Cooperation Name",
    value: "cooperation_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Influencer Id",
    value: "influencer_id",
    type: "text",
    hidden: true,
  },
  {
    label: "Cooperation Id",
    value: "cooperation_id",
    type: "text",
    hidden: true,
  },
  { label: "Channel", value: "placement_source", type: "text" },
  { label: "Placement Type", value: "placement_type", type: "text" },
  {
    label: "Link Id",
    value: "cooperation_link_id",
    type: "text",
    hidden: true,
  },
  { label: "Ad Spend", value: "total_spend", inputLabel: "€", type: "number" },
  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  { label: "Orders", value: "customer_purchase", type: "number" },

  { label: "ROAS", value: "return_on_ad_spend", type: "number" },
  {
    label: "CTR",
    value: "click_through_rate",
    inputLabel: "%",
    type: "number",
  },
  {
    label: "CAC",
    value: "customer_acquisition_cost",
    inputLabel: "€",
    type: "number",
  },
  { label: "CPO", value: "cost_per_order", inputLabel: "€", type: "number" },
];

export const GENERAL_INFLUENCER_FILTERS = [
  {
    label: "Influencer Name",
    value: "influencer_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Cooperation Name",
    value: "cooperation_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Influencer Id",
    value: "influencer_id",
    type: "text",
    hidden: true,
  },
  {
    label: "Cooperation Id",
    value: "cooperation_id",
    type: "text",
    hidden: true,
  },
  {
    label: "Link Id",
    value: "cooperation_link_id",
    type: "text",
    hidden: true,
  },
  { label: "Ad Spend", value: "total_spend", inputLabel: "€", type: "number" },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  { label: "Conversions", value: "customer_purchase", type: "number" },

  { label: "ROAS", value: "return_on_ad_spend", type: "number" },
  {
    label: "CTR",
    value: "click_through_rate",
    inputLabel: "%",
    type: "number",
  },
  { label: "CPA", value: "cost_per_order", inputLabel: "€", type: "number" },
];

export const CREATIVE_COMPARISON_OPTIONS = [
  { label: "Ad Spend", value: "total_spend", suffix: "€" },
  {
    label: "Revenue",
    value: "customer_revenue",
    suffix: "€",
  },
  { label: "Orders", value: "customer_purchase", type: "number" },

  { label: "ROAS", value: "return_on_ad_spend" },
  { label: "CPC", value: "cost_per_click", suffix: "€" },
  { label: "CPM", value: "cost_per_mille", suffix: "€" },
  { label: "CTR", value: "click_through_rate", suffix: "%" },
  { label: "CAC", value: "customer_acquisition_cost", suffix: "€" },
  {
    label: "New Customer Rate",
    value: "new_customer_purchase_ratio",
    suffix: "%",
  },
  { label: "CPO", value: "cost_per_order", suffix: "€" },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    suffix: "%",
  },
];
// for general dashboard mode
export const GENERAL_CREATIVE_COMPARISON_OPTIONS = [
  { label: "Ad Spend", value: "total_spend", suffix: "€" },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    suffix: "€",
  },
  { label: "Conversions", value: "customer_purchase", type: "number" },
  { label: "ROAS", value: "return_on_ad_spend" },
  { label: "CPC", value: "cost_per_click", suffix: "€" },
  { label: "CPM", value: "cost_per_mille", suffix: "€" },
  { label: "CTR", value: "click_through_rate", suffix: "%" },
  { label: "CAC", value: "customer_acquisition_cost", suffix: "€" },
  {
    label: "New Customer Rate",
    value: "new_customer_purchase_ratio",
    suffix: "%",
  },
  { label: "CPA", value: "cost_per_order", suffix: "€" },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    suffix: "%",
  },
];

export const METRIC_SUFFIXES: { [key: string]: string } = {
  customer_revenue: "€",
  total_spend: "€",
  click_through_rate: "%",
  customer_acquisition_cost: "€",
  cost_per_order: "€",
  average_order_value: "€",
  cost_per_mille: "€",
  cost_per_click: "€",
  cost_per_view: "€",
  conversion_rate: "%",
  new_customer_purchase_ratio: "%",
  new_visitor_pageview_ratio: "%",
  thumbstop_ratio: "%",
  video_avg_time_watched_actions: "s",
  influencer_fixed_commission: "€",
  influencer_variable_commission: "€",
};

export const METRIC_DECIMALS: { [key: string]: number } = {
  customer_revenue: 2,
  total_spend: 2,
  influencer_fixed_commission: 2,
  influencer_variable_commission: 2,
  click_through_rate: 2,
  customer_acquisition_cost: 2,
  cost_per_order: 2,
  average_order_value: 2,
  cost_per_mille: 2,
  cost_per_click: 2,
  cost_per_view: 2,
  conversion_rate: 2,
  new_customer_purchase_ratio: 2,
  new_visitor_pageview_ratio: 2,
  thumbstop_ratio: 2,
  video_avg_time_watched_actions: 2,
  return_on_ad_spend: 2,
  acquisition_return_on_ad_spend: 2,
  marketing_efficiency_ratio: 2,
  customer_purchase: 2,
  customer_unique_productview: 2,
  customer_unique_pageview: 2,
  customer_unique_addtocart: 2,
  customer_addtocart: 2,
  customer_productview: 2,
  customer_pageview: 2,
  frequency: 2,
  clicks: 0,
  link_click: 0,
  reach: 0,
  impressions: 0,
};

// Metric options for Insights Dashboard tiles
// isFixed: true means the metric is an ad platform metric (FixedKpiMetric) and only has totals, not new/returning segmentation
export const INSIGHTS_METRIC_OPTIONS = [
  {
    label: "Revenue",
    value: "customer_revenue",
    suffix: "currency",
    type: "currency",
    isFixed: false,
  },
  {
    label: "Ad Spend",
    value: "total_spend",
    suffix: "currency",
    type: "currency",
    isFixed: false,
  },
  {
    label: "ROAS",
    value: "return_on_ad_spend",
    suffix: "",
    type: "number",
    isFixed: false,
  },
  {
    label: "Orders",
    value: "customer_purchase",
    suffix: "",
    type: "number",
    isFixed: false,
  },
  {
    label: "AOV",
    value: "average_order_value",
    suffix: "currency",
    type: "currency",
    isFixed: false,
  },
  {
    label: "CAC",
    value: "customer_acquisition_cost",
    suffix: "currency",
    type: "currency",
    isFixed: false,
  },
  {
    label: "CPO",
    value: "cost_per_order",
    suffix: "currency",
    type: "currency",
    isFixed: false,
  },
  {
    label: "CTR",
    value: "click_through_rate",
    suffix: "%",
    type: "percentage",
    isFixed: true,
  },
  {
    label: "CPC",
    value: "cost_per_click",
    suffix: "currency",
    type: "currency",
    isFixed: true,
  },
  {
    label: "CPM",
    value: "cost_per_mille",
    suffix: "currency",
    type: "currency",
    isFixed: true,
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    suffix: "%",
    type: "percentage",
    isFixed: false,
  },
  {
    label: "New Customer Rate",
    value: "new_customer_purchase_ratio",
    suffix: "%",
    type: "percentage",
    isFixed: false,
  },
  {
    label: "Impressions",
    value: "impressions",
    suffix: "",
    type: "number",
    isFixed: true,
  },
  {
    label: "Clicks",
    value: "clicks",
    suffix: "",
    type: "number",
    isFixed: true,
  },
] as const;

export type InsightsMetricValue =
  (typeof INSIGHTS_METRIC_OPTIONS)[number]["value"];

export const INFLUENCER_PERFORMANCE_OPTIONS = [
  { label: "Ad Spend", value: "total_spend", suffix: "€" },
  {
    label: "Revenue",
    value: "customer_revenue",
    suffix: "€",
  },
  {
    label: "Orders",
    value: "customer_purchase",
    suffix: "",
  },
  { label: "ROAS", value: "return_on_ad_spend" },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    suffix: "%",
  },
  { label: "CPO", value: "cost_per_order", suffix: "€" },
  { label: "CAC", value: "customer_acquisition_cost", suffix: "€" },
  {
    label: "New Customer Rate",
    value: "new_customer_purchase_ratio",
    suffix: "%",
  },
];

export const GENERAL_INFLUENCER_PERFORMANCE_OPTIONS = [
  { label: "Ad Spend", value: "total_spend", suffix: "€" },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    suffix: "€",
  },
  {
    label: "Conversions",
    value: "customer_purchase",
    suffix: "",
  },
  { label: "ROAS", value: "return_on_ad_spend" },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    suffix: "%",
  },
  { label: "CPA", value: "cost_per_order", suffix: "€" },
  { label: "CAC", value: "customer_acquisition_cost", suffix: "€" },
  {
    label: "New Customer Rate",
    value: "new_customer_purchase_ratio",
    suffix: "%",
  },
];

export const CURRENCY_METRICS = [
  "customer_revenue",
  "total_spend",
  "cost_per_order",
  "cost_per_mille",
  "customer_acquisition_cost",
  "average_order_value",
  "cost_per_click",
];

// Channel-specific RT filters
export const RT_FILTERS_EMAIL = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Orders",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const RT_FILTERS_ORGANIC = [
  {
    label: "Domain name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Orders",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const RT_FILTERS_INFLUENCER = [
  {
    label: "Influencer",
    value: "influencer_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Cooperation",
    value: "cooperation_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Orders",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

// General versions for each channel
export const GENERAL_RT_FILTERS_EMAIL = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversions",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const GENERAL_RT_FILTERS_ORGANIC = [
  {
    label: "Domain name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversions",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const GENERAL_RT_FILTERS_INFLUENCER = [
  {
    label: "Influencer",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Cooperation",
    value: "adset_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversions",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const CONVERSION_PATH_FILTERS = [
  {
    label: "Number of touchpoints",
    value: "number_of_touchpoints",
    inputLabel: "",
    type: "number",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },

  {
    label: "First touchpoint",
    value: "first::channel_conversion_path",
    type: "select",
    options: TOUCHPOINT_OPTIONS as Mutable<typeof TOUCHPOINT_OPTIONS>,
    availableFilter: [FilterTypesEnum.in_at],
  },
  {
    label: "Last touchpoint",
    value: "last::channel_conversion_path",
    type: "select",
    options: TOUCHPOINT_OPTIONS as Mutable<typeof TOUCHPOINT_OPTIONS>,
    availableFilter: [FilterTypesEnum.in_at],
  },
  {
    label: "Included touchpoint",
    value: "channel_conversion_path",
    type: "select",
    options: TOUCHPOINT_OPTIONS as Mutable<typeof TOUCHPOINT_OPTIONS>,
    availableFilter: [FilterTypesEnum.contains_any],
  },
  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Orders",
    value: "customer_purchase",
    inputLabel: "",
    type: "number",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
  {
    label: "Average Order Value",
    value: "average_order_value",
    type: "number",
    inputLabel: "€",
  },
  {
    label: "Avg. Time Between Touchpoints",
    value: "average_seconds_to_next",
    type: "time",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
  {
    label: "Avg. Journey Length",
    value: "average_seconds_to_conversion",
    type: "time",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
  {
    label: "Number of Customers",
    value: "number_of_customers",
    inputLabel: "",
    type: "number",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
];

export const GENERAL_CONVERSION_PATH_FILTERS = [
  {
    label: "Number of touchpoints",
    value: "number_of_touchpoints",
    inputLabel: "",
    type: "number",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
  {
    label: "First touchpoint",
    value: "first::channel_conversion_path",
    type: "select",
    options: TOUCHPOINT_OPTIONS as Mutable<typeof TOUCHPOINT_OPTIONS>,
    availableFilter: [FilterTypesEnum.in_at],
  },
  {
    label: "Last touchpoint",
    value: "last::channel_conversion_path",
    type: "select",
    options: TOUCHPOINT_OPTIONS as Mutable<typeof TOUCHPOINT_OPTIONS>,
    availableFilter: [FilterTypesEnum.in_at],
  },
  {
    label: "Included touchpoint",
    value: "channel_conversion_path",
    type: "select",
    options: TOUCHPOINT_OPTIONS as Mutable<typeof TOUCHPOINT_OPTIONS>,
    availableFilter: [FilterTypesEnum.contains_any],
  },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversions",
    value: "customer_purchase",
    inputLabel: "",
    type: "number",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
  {
    label: "Average Conversion Value",
    value: "average_order_value",
    type: "number",
    inputLabel: "€",
  },
  {
    label: "Avg. Time Between Touchpoints",
    value: "average_seconds_to_next",
    type: "time",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
  {
    label: "Avg. Journey Length",
    value: "average_seconds_to_conversion",
    type: "time",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
  {
    label: "Number of Customers",
    value: "number_of_customers",
    inputLabel: "",
    type: "number",
    availableFilter: [FilterTypesEnum.gt, FilterTypesEnum.lt],
  },
];

export const RT_FILTERS_TABOOLA_CRITEO = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Ad Name",
    value: "ad_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  { label: "Campaign Id", value: "campaign_id", type: "text" },
  { label: "Ad Id", value: "ad_id", type: "text" },

  {
    label: "Revenue",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Orders",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const GENERAL_RT_FILTERS_TABOOLA_CRITEO = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  {
    label: "Ad Name",
    value: "ad_name",
    type: "text",
    availableFilter: [FilterTypesEnum.icontains, FilterTypesEnum.not_icontains],
  },
  { label: "Campaign Id", value: "campaign_id", type: "text" },
  { label: "Ad Id", value: "ad_id", type: "text" },
  {
    label: "Conv. Value",
    value: "customer_revenue",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversions",
    value: "customer_purchase",
    type: "number",
  },
  {
    label: "AOV",
    value: "average_order_value",
    inputLabel: "€",
    type: "number",
  },
  {
    label: "Conversion Rate",
    value: "conversion_rate",
    inputLabel: "%",
    type: "number",
  },
];

export const PRESET_STATUS_MAPPING = {
  READY: "Ready",
  REPROCESSING: "Recalculating",
  PROCESSING: "Processing",
  REGISTERED: "Registered",
  PREFILLING: "Prefilling",
};

export const PRESET_STATUS_READY_TO_USE: PRESET_STATUS_ENUM[] = [
  PRESET_STATUS_ENUM.READY,
  PRESET_STATUS_ENUM.REPROCESSING,
  PRESET_STATUS_ENUM.PREFILLING,
];

// Helper function to get channel-specific filters
export const getChannelSpecificRTFilters = (
  channel: string,
  isGeneral: boolean = false
) => {
  if (isGeneral) {
    switch (channel) {
      case "email":
        return GENERAL_RT_FILTERS_EMAIL;
      case "whatsapp":
        return GENERAL_RT_FILTERS_EMAIL;
      case "organic":
        return GENERAL_RT_FILTERS_ORGANIC;
      case "influencer":
        return GENERAL_RT_FILTERS_INFLUENCER;
      case "taboola":
        return GENERAL_RT_FILTERS_TABOOLA_CRITEO;
      case "criteo":
        return GENERAL_RT_FILTERS_TABOOLA_CRITEO;
      default:
        return GENERAL_RT_FILTERS;
    }
  } else {
    switch (channel) {
      case "email":
        return RT_FILTERS_EMAIL;
      case "whatsapp":
        return RT_FILTERS_EMAIL;
      case "organic":
        return RT_FILTERS_ORGANIC;
      case "influencer":
        return RT_FILTERS_INFLUENCER;
      case "taboola":
        return RT_FILTERS_TABOOLA_CRITEO;
      case "criteo":
        return RT_FILTERS_TABOOLA_CRITEO;
      default:
        return RT_FILTERS;
    }
  }
};

export const mapChannelToColor = (
  channel: string,
  overrides?: Record<string, string>
) => {
  if (overrides && channel) {
    const normalized = channel.toLowerCase().replace(/[-_\s]/g, "");
    const override = overrides[normalized];
    if (override) return override;
  }
  return (
    {
      Meta: "#0668E1",
      Instagram: "#E1306C",
      YouTube: "#FF0000",
      Blog: "#F3B35A",
      Google: "#DB4437",
      TikTok: "#25F4EE",
      Email: "#46B8E3",
      Organic: "#65A30D",
      Referral: "#65A30D",
      Influencer: "#C13584",
      "Influencer (old)": "#C13584",
      Taboola: "#00659F",
      Pinterest: "#E60023",
      Snapchat: "#C4C116",
      Microsoft: "#008373",
      "Microsoft Ads": "#008373",
      WhatsApp: "#25D366",
      Messenger: "#25D366",
      Criteo: "#FE5000",
      Outbrain: "#EE6513",
      Direct: "#E3BD00",
      PERFORMANCE_MAX: "#E37400",
      SMART: "#673AB7",
      SEARCH: "#4285F4",
      DISCOVERY: "#0F9D58",
      DISPLAY: "#F4B400",
      SHOPPING: "#DB4437",
      VIDEO: "#FF0000",
    }[channel] ?? "#999"
  );
};

// Ad managers covered by the tracking-parameter-errors feature. The
// tracking_parameter_errors flag payload may restrict visibility to a
// subset via its `managers` key.
export const TRACKING_PARAMETER_SUPPORTED_SOURCES: AdAccountChannels[] = [
  "facebook",
  "google",
  "tiktok",
  "pinterest",
  "msads",
  "taboola",
  "outbrain",
];

export { PRESET_STATUS_ENUM, AVAILABLE_MARKETING_CHANNEL_OPTIONS };
